import { useCallback, useEffect, useRef, type RefObject } from 'react';
import { useDrawingStore } from '@/features/drawing/model';

interface UseCanvasTransformProps {
  containerRef: RefObject<HTMLDivElement | null>;
  canvasRef: RefObject<HTMLCanvasElement | null>;
  minZoomRef: RefObject<number>;
  isMobile: boolean;
}

export function useCanvasTransform({
  containerRef,
  canvasRef,
  minZoomRef,
  isMobile,
}: UseCanvasTransformProps) {
  const { canvas, brushSettings, setCanvasTransform, setBrushSize } =
    useDrawingStore();

  // Keep transform ref in sync with store
  const transformRef = useRef({ zoom: 1, pan: { x: 0, y: 0 } });
  const lastWheelEventRef = useRef<number>(0);

  useEffect(() => {
    transformRef.current = { ...canvas.transform };
  }, [canvas.transform]);

  // Custom zoom handlers that respect dynamic minZoom.
  // The canvas maps as `pan + zoom * point` around the viewport centre, so keeping the
  // centre of the viewport fixed while zooming means scaling the pan by the same factor.
  const zoomAroundCenter = useCallback(
    (newZoom: number) => {
      const { zoom, pan } = transformRef.current;
      if (zoom <= 0 || newZoom === zoom) return;
      const factor = newZoom / zoom;
      const newTransform = {
        zoom: newZoom,
        pan: { x: pan.x * factor, y: pan.y * factor },
      };
      transformRef.current = newTransform;
      setCanvasTransform(newTransform);
    },
    [setCanvasTransform]
  );

  const handleZoomIn = useCallback(() => {
    zoomAroundCenter(Math.min(canvas.transform.zoom * 1.5, 5));
  }, [canvas.transform.zoom, zoomAroundCenter]);

  const handleZoomOut = useCallback(() => {
    zoomAroundCenter(Math.max(canvas.transform.zoom / 1.5, minZoomRef.current));
  }, [canvas.transform.zoom, zoomAroundCenter, minZoomRef]);

  // Convert screen coordinates to canvas coordinates
  const screenToCanvas = useCallback(
    (clientX: number, clientY: number) => {
      const rect = canvasRef.current?.getBoundingClientRect();
      const containerRect = containerRef.current?.getBoundingClientRect();
      if (!rect || !containerRect || !canvasRef.current) return { x: 0, y: 0 };

      // Validate containerRect dimensions
      if (containerRect.width < 100 || containerRect.height < 100) {
        return { x: 0, y: 0 };
      }

      // Validate canvas dimensions
      if (canvasRef.current.width === 0 || canvasRef.current.height === 0) {
        return { x: 0, y: 0 };
      }

      const currentTransform = transformRef.current;

      // Validate zoom is reasonable
      if (currentTransform.zoom < 0.01 || currentTransform.zoom > 10) {
        return { x: 0, y: 0 };
      }

      // Get container center
      const centerX = containerRect.width / 2;
      const centerY = containerRect.height / 2;

      // Mouse position relative to container center
      const relativeX = clientX - containerRect.left - centerX;
      const relativeY = clientY - containerRect.top - centerY;

      // Apply inverse transformations to get canvas coordinates
      const canvasX =
        (relativeX - currentTransform.pan.x) / currentTransform.zoom +
        canvasRef.current.width / 2;
      const canvasY =
        (relativeY - currentTransform.pan.y) / currentTransform.zoom +
        canvasRef.current.height / 2;

      return { x: canvasX, y: canvasY };
    },
    [canvasRef, containerRef]
  );

  // Helper function to reposition canvas if text block is in lower half on mobile
  const repositionCanvasIfNeeded = useCallback(
    (blockCanvasPoint: { x: number; y: number }) => {
      if (!isMobile) return;

      const container = containerRef.current;
      const canvasEl = canvasRef.current;
      if (!container || !canvasEl) return;

      const containerRect = container.getBoundingClientRect();
      const centerY = containerRect.height / 2;
      const currentTransform = transformRef.current;

      // Calculate screen position of the text block
      const relativeY =
        (blockCanvasPoint.y - canvasEl.height / 2) * currentTransform.zoom +
        currentTransform.pan.y;

      const screenY = relativeY + centerY;

      // Check if block is in lower half of screen
      const isInLowerHalf = screenY > containerRect.height / 2;

      if (isInLowerHalf) {
        const targetScreenY = containerRect.height * 0.4;
        const deltaY = targetScreenY - screenY;
        const newPanY = currentTransform.pan.y + deltaY;

        const newTransform = {
          zoom: currentTransform.zoom,
          pan: { x: currentTransform.pan.x, y: newPanY },
        };

        transformRef.current = newTransform;
        setCanvasTransform(newTransform);
      }
    },
    [isMobile, setCanvasTransform, containerRef, canvasRef]
  );

  // Wheel event for zoom and tool size
  const handleWheel = useCallback(
    (e: WheelEvent) => {
      e.preventDefault();

      if (!containerRef.current) return;

      // Simple throttling - limit to ~60fps
      const now = Date.now();
      if (now - lastWheelEventRef.current < 16) return;
      lastWheelEventRef.current = now;

      if (e.ctrlKey) {
        // Change tool size with Ctrl+wheel
        const delta = e.deltaY > 0 ? -5 : 5;
        const currentSize = brushSettings.size;
        const newSize = Math.max(1, Math.min(100, currentSize + delta));
        setBrushSize(newSize);
      } else {
        // Get container bounds
        const containerRect = containerRef.current.getBoundingClientRect();

        // Mouse position relative to the container centre (the container is not
        // necessarily flush with the viewport, so its origin has to come off first).
        const mouseX = e.clientX - containerRect.left - containerRect.width / 2;
        const mouseY = e.clientY - containerRect.top - containerRect.height / 2;

        const currentZoom = transformRef.current.zoom;
        const currentPan = transformRef.current.pan;

        // Smooth zoom speed
        const delta = e.deltaY > 0 ? 0.97 : 1.03;
        const newZoom = Math.min(
          Math.max(currentZoom * delta, minZoomRef.current),
          5
        );

        // The point under the mouse in the scaled coordinate system
        const pointX = (mouseX - currentPan.x) / currentZoom;
        const pointY = (mouseY - currentPan.y) / currentZoom;

        // After scaling, we want the same point to remain under the mouse
        const newPanX = mouseX - pointX * newZoom;
        const newPanY = mouseY - pointY * newZoom;

        const newTransform = {
          zoom: newZoom,
          pan: { x: newPanX, y: newPanY },
        };
        transformRef.current = newTransform;
        setCanvasTransform(newTransform);
      }
    },
    [
      brushSettings.size,
      setBrushSize,
      setCanvasTransform,
      containerRef,
      minZoomRef,
    ]
  );

  // Add wheel event listener with passive: false to container
  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      container.addEventListener('wheel', handleWheel, { passive: false });
      return () => {
        container.removeEventListener('wheel', handleWheel);
      };
    }
    return undefined;
  }, [handleWheel, containerRef]);

  return {
    transformRef,
    screenToCanvas,
    handleZoomIn,
    handleZoomOut,
    repositionCanvasIfNeeded,
  };
}
